import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Theme,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';

import FormSection from '../../../components/form/FormSection';
import ButtonRow from '../../../components/ButtonRow';
import Page from '../../../components/Page';
import {
  MinimalLessonLocation,
  Operator,
  User,
  UserDTO,
  ValidationViolation,
} from '../../../types';
import OperatorSelect from '../../admin/forms/OperatorSelect';
import { ACCOUNT_BASE_ROUTE, USER_BASE_ROUTE } from '../index';
import UserRepository from '../repository/UserRepository';
import { userToUserDTO } from '../utils';
import {
  hasViolationForField,
  isValidationErrorResponse,
} from '../../../utils/common';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
}));

interface EditUserProps {
  id?: string;
  isProfile: boolean;
}

const EditUser = (props: EditUserProps) => {
  const { id: passedId, isProfile } = props;
  const { id: paramId } = useParams<{ id?: string }>();
  const id = passedId || paramId || '';

  const classes = useStyles();
  const notifications = useSnackbar();
  const history = useHistory();

  const account = useSelector(
    (selector: {
      user: {
        account: User;
      };
    }) => selector.user.account,
  );
  const isSuperAdmin = account.roles.includes('ROLE_SUPER_ADMIN');
  const isAdmin = account.roles.includes('ROLE_ADMIN');
  const isSLPManager = account.roles.includes(
    'ROLE_SWIMMING_LESSON_PROVIDER_MANAGER',
  );

  const [user, setUser] = useState<User | null>(null);
  const [userDto, setUserDto] = useState<UserDTO>({
    id: null,
    email: '',
    role: 'ROLE_ADMIN',
    swimmingLessonProviderId: null,
    lessonLocationIds: [],
    firstName: '',
    insertion: null,
    lastName: '',
    operatorId: null,
  });

  const baseUrl = isProfile ? ACCOUNT_BASE_ROUTE : `${USER_BASE_ROUTE}/${id}`;
  const pageTitle = isProfile ? 'Profiel bewerken' : 'Gebruiker bewerken';
  const showBreadcrumbs = !isProfile;

  const [selectedLessonLocations, setSelectedLessonLocations] = useState<
    MinimalLessonLocation[]
  >([]);

  const [violations, setViolations] = useState<ValidationViolation[]>([]);

  const selectOperator = (operator: Operator | null) => {
    setUserDto((prev) => ({
      ...prev,
      operatorId: operator ? operator.id : null,
    }));
  };

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    setUserDto((prev) => ({
      ...prev,
      [event.target.name as string]: event.target.value,
    }));
  };

  const handleLessonLocationsChange = (
    _event: any,
    updatedLessonLocations: MinimalLessonLocation[],
  ) => {
    setSelectedLessonLocations(updatedLessonLocations);
    setUserDto((prev) => ({
      ...prev,
      lessonLocationIds: updatedLessonLocations.map(
        (lessonLocation) => lessonLocation.id,
      ),
    }));
  };

  const handleMayInvoiceChange = (
    event: React.ChangeEvent<{ name?: string; checked: boolean }>,
  ) => {
    setUserDto((prev) => {
      if (!prev.delegateProfile) {
        return prev;
      }

      return {
        ...prev,
        delegateProfile: {
          ...prev.delegateProfile,
          mayInvoice: event.target.checked,
        },
      };
    });
  };

  const handleMaxDistanceChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const value = parseFloat(`${event.target.value}`.replace(/[^\d.-]/g, ''));

    setUserDto((prev) => {
      if (!prev.delegateProfile) {
        return prev;
      }

      return {
        ...prev,
        delegateProfile: {
          ...prev.delegateProfile,
          maxDistanceExamsInKm: value as number,
        },
      };
    });
  };

  const handleSubmit = () => {
    new UserRepository()
      .update(id, userDto)
      .then(() => {
        history.push(baseUrl);
        notifications.enqueueSnackbar('Gebruiker is succesvol opgeslagen!', {
          variant: 'success',
        });
      })
      .catch((error) => {
        if (isValidationErrorResponse(error)) {
          const { violations: newViolations } = error.response.data;
          setViolations(newViolations);
        } else {
          notifications.enqueueSnackbar(
            'Er is iets fout gegaan bij het opslaan van de gebruiker.',
            { variant: 'error' },
          );
        }
      });
  };

  useEffect(() => {
    new UserRepository()
      .find(id)
      .then((res) => {
        setUser(res.data);
        setUserDto(userToUserDTO(res.data));
        setSelectedLessonLocations(res.data.grantedLessonLocations ?? []);
      })
      .catch(() => history.push(baseUrl));
  }, [id]);

  let mayInvoiceCheckbox = null;
  if (userDto.delegateProfile) {
    mayInvoiceCheckbox = (
      <Checkbox
        name="mayInvoice"
        checked={userDto.delegateProfile.mayInvoice}
        onChange={handleMayInvoiceChange}
      />
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Page title={pageTitle} breadcrumbs={showBreadcrumbs}>
      <FormSection title="Gegevens">
        {(isAdmin || isSLPManager) &&
        user.id !== account.id &&
        user.roles.includes('ROLE_LESSON_LOCATION_USER') &&
        user.swimmingLessonProvider ? (
          <Box>
            <FormControl fullWidth>
              <FormLabel>Leslocatie(s)</FormLabel>
              <Box mt={2}>
                <Autocomplete
                  multiple
                  onChange={handleLessonLocationsChange}
                  value={selectedLessonLocations}
                  options={user.swimmingLessonProvider.lessonLocations}
                  getOptionLabel={(option) => option.name}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Selecteer optioneel één of meerdere leslocaties"
                      error={hasViolationForField(
                        'lessonLocationIds',
                        violations,
                      )}
                    />
                  )}
                />
              </Box>
            </FormControl>
          </Box>
        ) : null}
        {isSuperAdmin && user.operator ? (
          <OperatorSelect
            onSelect={selectOperator}
            initialValue={user.operator}
          />
        ) : null}
        <TextField
          label="E-mail"
          className={classes.field}
          value={userDto.email}
          name="email"
          error={hasViolationForField('email', violations)}
          onChange={handleChange}
          fullWidth
          required
        />
        <TextField
          label="Voornaam"
          className={classes.field}
          value={userDto.firstName}
          name="firstName"
          error={hasViolationForField('firstName', violations)}
          onChange={handleChange}
          fullWidth
          required
        />
        <TextField
          label="Tussenvoegsel"
          className={classes.field}
          value={userDto.insertion}
          name="insertion"
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Achternaam"
          className={classes.field}
          value={userDto.lastName}
          name="lastName"
          error={hasViolationForField('lastName', violations)}
          onChange={handleChange}
          fullWidth
          required
        />
      </FormSection>
      {isAdmin && userDto.delegateProfile && (
        <FormSection title="Gedelegeerdenprofiel">
          <>
            {mayInvoiceCheckbox && (
              <FormGroup>
                <FormControlLabel
                  control={mayInvoiceCheckbox}
                  label="Mag factureren"
                />
              </FormGroup>
            )}
          </>
          <TextField
            type="number"
            label="Maximale afstand tot examens in km"
            className={classes.field}
            value={userDto.delegateProfile.maxDistanceExamsInKm}
            name="maxDistanceToExams"
            onChange={handleMaxDistanceChange}
            fullWidth
          />
        </FormSection>
      )}
      <ButtonRow>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          startIcon={<FontAwesomeIcon icon={['fad', 'save']} />}
          onClick={handleSubmit}
        >
          Opslaan
        </Button>
      </ButtonRow>
    </Page>
  );
};

export default EditUser;
